import React from "react";
import { Box, Container, Flex, Heading, Text, Grid, Image, Link } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

const Home: React.FC = () => {
    const primaryColor = "#1d4e4e";
    const textColor = "#333333";
    const white = "#ffffff";

    return (
        <Box>
            {/* Header */}
            <Box
                as="header"
                bg={white}
                boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
                position="fixed"
                width="100%"
                top={0}
                zIndex={1000}
            >
                <Container maxW="1200px">
                    <Flex justify="space-between" align="center" py={4}>
                        <RouterLink
                            to="/"
                            style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                        >
                            <Image
                                src="/assets/logos/logo.png"
                                alt="Grade-R Logo"
                                w="40px"
                                h="40px"
                            />
                            <Text color={primaryColor} fontSize="1.5rem" fontWeight="700">
                                Grade-R
                            </Text>
                        </RouterLink>
                        <Flex gap={8}>
                            <RouterLink to="/help" style={{ color: textColor, fontWeight: 500 }}>
                                Help
                            </RouterLink>
                            <RouterLink to="/privacy" style={{ color: textColor, fontWeight: 500 }}>
                                Privacy Policy
                            </RouterLink>
                            <RouterLink to="/delete" style={{ color: textColor, fontWeight: 500 }}>
                                Account Deletion
                            </RouterLink>
                        </Flex>
                    </Flex>
                </Container>
            </Box>

            {/* Hero Section */}
            <Box
                as="section"
                pt="8rem"
                pb="4rem"
                bg="linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)"
            >
                <Container maxW="1200px">
                    <Grid
                        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                        gap={16}
                        alignItems="center"
                    >
                        <Box>
                            <Heading
                                as="h1"
                                fontSize="3rem"
                                color={primaryColor}
                                mb={6}
                                lineHeight="1.2"
                            >
                                Smart Grading Made Simple
                            </Heading>
                            <Text fontSize="1.2rem" mb={8} color="#666">
                                Grade-R is your all-in-one solution for efficient grade management.
                                Streamline your grading process, track student progress, and make
                                data-driven decisions with ease.
                            </Text>
                            <Flex gap={4} mb={8}>
                                <Link
                                    href="https://play.google.com/store/apps/details?id=com.shedeurapps.grader"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        padding: "0.8rem 1.5rem",
                                        backgroundColor: primaryColor,
                                        color: white,
                                        textDecoration: "none",
                                        borderRadius: "8px",
                                        fontWeight: 500,
                                        transition: "transform 0.3s ease",
                                    }}
                                    _hover={{ transform: "translateY(-2px)" }}
                                >
                                    <Image
                                        src="/assets/images/google-play.webp"
                                        w="24px"
                                        h="28px"
                                    />
                                    Get it on Google Play
                                </Link>

                                <Link
                                    href="https://grader-webapp.web.app"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        padding: "0.8rem 1.5rem",
                                        backgroundColor: primaryColor,
                                        color: white,
                                        textDecoration: "none",
                                        borderRadius: "8px",
                                        fontWeight: 500,
                                        transition: "transform 0.3s ease",
                                    }}
                                    _hover={{ transform: "translateY(-2px)" }}
                                >
                                    <Image src="/assets/images/web.png" w="24px" h="28px" />
                                    Try The WebApp
                                </Link>
                            </Flex>
                        </Box>
                        <Box>
                            <Image
                                src="/assets/images/preview.png"
                                alt="Grade-R App Preview"
                                maxW="60%"
                            />
                        </Box>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box as="section" py={16} bg={white} id="features">
                <Container maxW="1200px">
                    <Heading
                        as="h2"
                        textAlign="center"
                        color={primaryColor}
                        fontSize="2rem"
                        mb={12}
                    >
                        Key Features
                    </Heading>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8}>
                        {/* Feature Cards */}
                        <Box
                            p={8}
                            borderRadius="12px"
                            boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                            _hover={{ transform: "translateY(-5px)" }}
                            transition="transform 0.3s ease"
                        >
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryColor}
                                strokeWidth="2"
                                style={{ marginBottom: "1rem" }}
                            >
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <Heading as="h3" color={primaryColor} mb={4}>
                                Easy Grade Management
                            </Heading>
                            <Text>
                                Quickly input and manage grades with an intuitive interface designed
                                specifically for educators.
                            </Text>
                        </Box>

                        <Box
                            p={8}
                            borderRadius="12px"
                            boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                            _hover={{ transform: "translateY(-5px)" }}
                            transition="transform 0.3s ease"
                        >
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryColor}
                                strokeWidth="2"
                                style={{ marginBottom: "1rem" }}
                            >
                                <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <Heading as="h3" color={primaryColor} mb={4}>
                                Progress Tracking
                            </Heading>
                            <Text>
                                Monitor student progress over time with detailed analytics and
                                performance insights.
                            </Text>
                        </Box>

                        <Box
                            p={8}
                            borderRadius="12px"
                            boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                            _hover={{ transform: "translateY(-5px)" }}
                            transition="transform 0.3s ease"
                        >
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryColor}
                                strokeWidth="2"
                                style={{ marginBottom: "1rem" }}
                            >
                                <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                            <Heading as="h3" color={primaryColor} mb={4}>
                                Customizable Grading
                            </Heading>
                            <Text>
                                Create custom grading scales and rubrics to match your specific
                                teaching requirements.
                            </Text>
                        </Box>
                    </Grid>
                </Container>
            </Box>

            {/* Footer */}
            <Box as="footer" bg={primaryColor} color={white} py={12}>
                <Container maxW="1200px">
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} mb={8}>
                        <Box>
                            <Heading as="h3" fontSize="1.2rem" mb={4}>
                                Grade-R
                            </Heading>
                            <Text>Smart grading solutions for tertiary students.</Text>
                        </Box>
                        <Box>
                            <Heading as="h3" fontSize="1.2rem" mb={4}>
                                Quick Links
                            </Heading>
                            <Box as="ul" listStyleType="none" style={{ margin: 0, padding: 0 }}>
                                <Box as="li" mb={2}>
                                    <RouterLink
                                        to="#features"
                                        style={{ color: white, opacity: 0.8 }}
                                    >
                                        Features
                                    </RouterLink>
                                </Box>
                                <Box as="li" mb={2}>
                                    <RouterLink
                                        to="/privacy"
                                        style={{ color: white, opacity: 0.8 }}
                                    >
                                        Privacy Policy
                                    </RouterLink>
                                </Box>
                                <Box as="li" mb={2}>
                                    <RouterLink to="/delete" style={{ color: white, opacity: 0.8 }}>
                                        Account Deletion
                                    </RouterLink>
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            <Heading as="h3" fontSize="1.2rem" mb={4}>
                                Contact
                            </Heading>
                            <Box as="ul" listStyleType="none" style={{ margin: 0, padding: 0 }}>
                                <Box as="li" mb={2}>
                                    <Link
                                        href="mailto:shedeurapps@gmail.com"
                                        color={white}
                                        opacity={0.8}
                                        _hover={{ opacity: 1 }}
                                    >
                                        shedeurapps@gmail.com
                                    </Link>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Box
                        textAlign="center"
                        mt={8}
                        pt={8}
                        borderTop="1px solid rgba(255, 255, 255, 0.1)"
                    >
                        <Text>&copy; 2024 Shedeur Apps. All rights reserved.</Text>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default Home;
